import { useMemo } from 'react'
import { useJumpLastUrl } from '@!/Hooks'
import { VStack, Text, Box, Link, List, ListItem, Center, Image, Stack, Divider, Grid, GridItem } from '@chakra-ui/react'
import { NormalButton } from '@!/components/Button'
import { ScrollableLatelyAccessList } from '@!/Store/Common/components/ScrollableLatelyAccessList'
import { buildAssetUrlString, getDevice } from '@!/Util'
import { AppStoreBadges } from '@!/Common/AppStoreBadges'
import medalTicketScreenCapture from '@images/medal_ticket_capture.webp'
import storeScreenCapture from '@images/store_capture.webp'

export const MonthlyCourseRegistrationComplete = (props) => {
  const jumpLastUrl = useJumpLastUrl()
  const device = useMemo(() => getDevice(), [])
  const { monthly_course_name, coin_count, reg_bonus_point_count, cont_bonus_point_count, reg_bonus_rate, cont_bonus_rate, charge_day, restore, error } =
    props.params
  let text = ''

  if (error) {
    switch (error) {
      case 'FAILED_REGISTER':
        text = device === 'sp' ? '月額コースの登録に失敗しました。\n再度登録を行ってください。' : '月額コースの登録に失敗しました。再度登録を行ってください。'
        break
      default:
        text =
          device === 'sp'
            ? 'エラーが発生しました。\nしばらく時間をおいて再度お試しください。'
            : 'エラーが発生しました。しばらく時間をおいて再度お試しください。'
    }
  }

  return (
    <VStack spacing={6} py={8}>
      {error ? (
        <Text textAlign={'center'} fontWeight={'semibold'} mx={4} my={10} whiteSpace={'pre-line'}>
          {text}
        </Text>
      ) : (
        <>
          <Text textAlign={'center'} fontWeight={'semibold'}>
            ご登録ありがとうございます。
            <br />
            下記月額コースの登録が完了いたしました。
          </Text>

          <Box px={4} w={'full'}>
            <Center>
              <Box border={'1px solid'} borderColor={'divider.light'} borderRadius={'sm'} maxW={'343px'} w={'full'}>
                <Grid fontSize={'xs'}>
                  <GridItem colSpan={2} bg={'gray.100'} py={1} textAlign={'center'} borderBottom={'1px solid'} borderColor={'divider.light'}>
                    <Text fontWeight={'bold'}>{monthly_course_name}</Text>
                  </GridItem>

                  <GridItem py={2} textAlign={'center'}>
                    <Text mb={2}>付与コイン</Text>
                    {!restore && <Text mb={2}>登録ボーナス</Text>}
                    <Text>継続ボーナス</Text>
                  </GridItem>

                  <GridItem py={2} textAlign={'center'} borderLeft={'1px solid'} borderColor={'divider.light'}>
                    <Text mb={2}>{coin_count}</Text>
                    {!restore && (
                      <Text mb={2}>
                        {reg_bonus_point_count}pt（{reg_bonus_rate}%）
                      </Text>
                    )}
                    <Text>
                      {cont_bonus_point_count}pt（{cont_bonus_rate}%）
                    </Text>
                  </GridItem>
                </Grid>
              </Box>
            </Center>

            <Center mt={5} mb={4}>
              <Text fontSize={'xs'}>
                <Link href={'/user/monthly_course_histories'}>月額コース履歴</Link>
                より、ご確認いただけます。
              </Text>
            </Center>

            <Box fontSize={'xs'}>
              <List styleType={'disc'} pl={4}>
                <ListItem>
                  次回以降の決済日は毎月{charge_day}日です。ただし、該当日がない月は月末最終日に自動決済されます。
                  <br />
                  （例：5月31日に登録した場合、6月は30日に決済されます。）
                </ListItem>
                <ListItem>次回以降のコインと継続ボーナスポイントは、自動決済日の翌日に付与されます。</ListItem>
              </List>
            </Box>
          </Box>
        </>
      )}

      <Center px={4} w={'full'}>
        {error ? (
          <NormalButton onClick={() => (window.location.href = '/user/payment')}>登録ページに戻る</NormalButton>
        ) : (
          <NormalButton onClick={jumpLastUrl}>元のページに戻る</NormalButton>
        )}
      </Center>

      <Divider />

      <Box w={'full'}>
        <Box px={4} w={'full'} position={'relative'}>
          <Box position={'absolute'} left={'50%'} transform={'translateX(-50%)'} bgColor={'bg.primary'} top={-10}>
            <Text textAlign={'center'} fontWeight={'extrabold'} fontFamily={'Hiragino Sans'} whiteSpace={'nowrap'} px={1}>
              WEBで購入したコイン・
              {device === 'sp' && <br />}
              貯まったポイントは、
              <br />
              <Text as={'span'} color={'primary.main'}>
                マンガBANG!(アプリ)
              </Text>
              内の
              <br />
              下記コーナーでもお使いいただけます。
            </Text>
          </Box>

          <Box border={'1px solid'} borderColor={'divider.dark'} borderRadius={'lg'} px={4} mt={9}>
            <Box bgColor={'gray.300'} borderRadius={'lg'} mt={16}>
              <Stack
                direction={{ base: 'column', md: 'row' }}
                divider={<Divider orientation={'vertical'} borderColor={'divider.dark'} h={'auto'} />}
                spacing={0}
              >
                <VStack spacing={0} py={4} px={2} pb={0} width={{ md: '50%' }} justifyContent={'space-between'}>
                  <Text fontSize={'lg'} fontWeight={'black'} mb={2}>
                    メダル・チケット作品
                  </Text>
                  <Image src={buildAssetUrlString(medalTicketScreenCapture)} alt={'メダル・チケット作品'} w={'full'} maxW={'287px'} />
                </VStack>
                <VStack spacing={0} py={4} px={2} pb={0} width={{ md: '50%' }} justifyContent={'space-between'}>
                  <Text fontSize={'lg'} fontWeight={'black'} mb={2}>
                    マンガBANGストア
                  </Text>
                  <Image src={buildAssetUrlString(storeScreenCapture)} alt={'マンガBANGストア'} maxW={'135px'} />
                </VStack>
              </Stack>
            </Box>
            <Box fontSize={'3xs'} py={4}>
              <Text>※ アプリでポイントをお使いいただくためには、事前にアプリで会員登録の上、マンガBANGブックスでログインが必要です。</Text>
              <Text>※ WEBで貯まったポイントは、1pt=1コインとしてご利用いただけます。</Text>
            </Box>
          </Box>
        </Box>

        {device !== 'pc' && (
          <Box px={4} w={'full'} position={'relative'}>
            <Box position={'absolute'} left={'50%'} transform={'translateX(-50%)'} bgColor={'bg.primary'} top={-3}>
              <Text fontWeight={'extrabold'} fontFamily={'Hiragino Sans'} whiteSpace={'nowrap'} px={2}>
                アプリのインストールはこちら
              </Text>
            </Box>
            <Box border={'1px solid'} borderColor={'divider.dark'} borderRadius={'lg'} py={2} mt={6}>
              <AppStoreBadges />
            </Box>
          </Box>
        )}
      </Box>

      <Divider />

      <Box w="100%">
        <ScrollableLatelyAccessList />
      </Box>
    </VStack>
  )
}
